import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI } from '../../../api';
import { resolveUrl, fmtDate, fmtDateTime, overlayStyle, modalStyle, labelStyle } from '../utils';

export const HistoryEntryView = ({ clientId, entryId, boards, documents, t, lang, onEdit, onDeleted, onClose, showToast }) => {
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clientsAPI.history.get(clientId, entryId).then(({ data }) => setEntry(data)).catch(() => onClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId]);

  if (!entry) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={{ ...modalStyle, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
      </div>
    );
  }

  const linkedBoard = entry.board_id ? boards.find((b) => b.id === entry.board_id) : null;
  const linkedDocs = (entry.document_ids || []).map((id) => documents.find((d) => d.id === id)).filter(Boolean);
  const edits = entry.edit_history || [];

  const handleDelete = async () => {
    if (!window.confirm(t.cs_delete_entry_confirm || 'Delete this entry permanently?')) return;
    try {
      await clientsAPI.history.remove(clientId, entry.id);
      showToast(t.cs_deleted || 'Removed');
      onDeleted();
    } catch (e) { /* noop */ }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, maxWidth: 720 }} onClick={(e) => e.stopPropagation()} data-testid="history-entry-view">
        <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#cf9a3d', margin: '0 0 8px', fontFamily: 'Playfair Display, serif' }}>
          {fmtDate(entry.date, lang)}
        </p>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, fontWeight: 400, margin: '0 0 18px', color: 'var(--kahla-charcoal)', lineHeight: 1.2 }}>
          {entry.title}
        </h2>

        {entry.note && (
          <p style={{ fontSize: 15, lineHeight: 1.75, color: '#4a463e', margin: '0 0 20px', whiteSpace: 'pre-wrap' }}>{entry.note}</p>
        )}

        {(entry.tags?.length > 0) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
            {entry.tags.map((tag) => (
              <span key={tag} style={{ padding: '3px 10px', fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', border: '1px solid var(--kahla-border)', borderRadius: 999 }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {(entry.photos?.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginBottom: 24 }} data-testid="entry-view-photos">
            {entry.photos.map((p) => (
              <a key={p.id} href={resolveUrl(p.url)} target="_blank" rel="noopener noreferrer">
                <img src={resolveUrl(p.url)} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
              </a>
            ))}
          </div>
        )}

        {(linkedBoard || linkedDocs.length > 0) && (
          <div style={{ borderTop: '1px solid var(--kahla-border)', paddingTop: 18, marginBottom: 20, display: 'grid', gap: 10 }}>
            {linkedBoard && (
              <button
                type="button" data-testid="entry-view-board-link"
                onClick={() => navigate(`/editor/${linkedBoard.id}`)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 13.5, color: 'var(--kahla-charcoal)' }}
              >
                <span style={labelStyle}>{t.cs_entry_link_board_view || 'Board'}</span>
                <span style={{ borderBottom: '1px solid var(--kahla-border)' }}>{linkedBoard.name} →</span>
              </button>
            )}
            {linkedDocs.length > 0 && (
              <div>
                <span style={labelStyle}>{t.cs_tab_documents || 'Documents'}</span>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {linkedDocs.map((d) => (
                    <a key={d.id} href={resolveUrl(d.file_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13.5, color: 'var(--kahla-charcoal)', borderBottom: '1px solid var(--kahla-border)', textDecoration: 'none' }}>
                      {d.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Meta: created / last edited / edit history */}
        <div style={{ borderTop: '1px solid var(--kahla-border)', paddingTop: 14, fontSize: 11.5, color: 'var(--kahla-text-light)', display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <span data-testid="entry-created">{t.cs_created || 'Created'}: {fmtDateTime(entry.created_at, lang)}</span>
          {entry.updated_at !== entry.created_at && (
            <span data-testid="entry-last-edited">{t.cs_last_edited || 'Last edited'}: {fmtDateTime(entry.updated_at, lang)}</span>
          )}
          {edits.length > 0 && (
            <button
              type="button" data-testid="entry-edit-history-toggle"
              onClick={() => setShowHistory(!showHistory)}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11.5, color: '#cf9a3d', fontFamily: 'inherit' }}
            >
              {t.cs_edit_history || 'Edit history'} ({edits.length}) {showHistory ? '▴' : '▾'}
            </button>
          )}
        </div>

        {showHistory && (
          <div data-testid="entry-edit-history" style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            {[...edits].reverse().map((v, i) => (
              <div key={i} style={{ borderLeft: '2px solid var(--kahla-border)', padding: '6px 0 6px 14px' }}>
                <p style={{ fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', margin: '0 0 3px' }}>
                  {t.cs_previous_version || 'Version until'} {fmtDateTime(v.edited_at, lang)}
                </p>
                <p style={{ fontSize: 13, color: '#4a463e', margin: 0 }}>
                  <strong>{v.title}</strong>{v.note ? ` — ${v.note.slice(0, 160)}${v.note.length > 160 ? '…' : ''}` : ''}
                </p>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'space-between' }}>
          <button
            type="button" data-testid="entry-delete-btn"
            onClick={handleDelete}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'Playfair Display, serif', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#b0483a' }}
          >{t.cs_delete || 'Delete'}</button>
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" className="btn-ghost" data-testid="entry-view-close-btn" onClick={onClose}>{t.cs_close || 'Close'}</button>
            <button type="button" className="btn-primary" data-testid="entry-edit-btn" onClick={() => onEdit(entry)}>{t.cs_edit || 'Edit'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryEntryView;
